import axiosInstance from "../axiosInstance";

export interface DashboardSummary {
  totalCount: number;
  notInjectedCount: number;
  injectedCount: number;
  completionRate: number;
  todayInjectSuccess: number;
  todayInjectFail: number;
}

export interface ModelStatus {
  terminalModelId: number;
  internalModelName: string;
  externalModelName: string;
  vanType: number;
  totalCount: number;
  notInjectedCount: number;
  injectedCount: number;
}

export interface ProductionTrendPoint {
  period: string;
  count: number;
}

export type TrendUnit = "day" | "month" | "year";

export interface ProductionTrendParams {
  unit: TrendUnit;
  startDate?: string;
  endDate?: string;
  vanType?: number;
  terminalModelId?: number;
}

export interface HourlyProductionPoint {
  hour: number;
  count: number;
}

export interface InjectionResult {
  keyType: number;
  successCount: number;
  failCount: number;
  totalCount: number;
  failRate: number;
}

export type DistributionDimension = "van" | "status" | "color";

export interface DistributionPoint {
  code: string;
  count: number;
}

export interface DateRangeParams {
  startDate?: string;
  endDate?: string;
}

export interface CommonCode {
  code: string;
  name: string;
}

export interface WorkerPerformance {
  userNo: number;
  userName: string;
  totalCount: number;
  successCount: number;
  failCount: number;
  successRate: number;
}

export interface WeekdayHourCount {
  weekday: number;
  hour: number;
  count: number;
}

export interface AuditSummary {
  eventCounts: DistributionPoint[];
  pendingWorkerCount: number;
  activeUserCount: number;
  workerCount: number;
}

const unwrap = <T>(res: { data?: { data?: T } }): T => {
  return (res.data?.data ?? null) as T;
};

export const fetchDashboardSummary = async (): Promise<DashboardSummary> => {
  const res = await axiosInstance.get("/api/v1/dashboard/summary");
  return unwrap<DashboardSummary>(res);
};

export const fetchModelStatus = async (): Promise<ModelStatus[]> => {
  const res = await axiosInstance.get("/api/v1/dashboard/model-status");
  return unwrap<ModelStatus[]>(res) ?? [];
};

export const fetchProductionTrend = async (
  params: ProductionTrendParams,
): Promise<ProductionTrendPoint[]> => {
  const res = await axiosInstance.get("/api/v1/dashboard/production-trend", {
    params,
  });
  return unwrap<ProductionTrendPoint[]>(res) ?? [];
};

export const fetchHourlyProduction = async (
  params: DateRangeParams = {},
): Promise<HourlyProductionPoint[]> => {
  const res = await axiosInstance.get("/api/v1/dashboard/hourly-production", {
    params,
  });
  return unwrap<HourlyProductionPoint[]>(res) ?? [];
};

export const fetchInjectionResult = async (
  params: DateRangeParams = {},
): Promise<InjectionResult[]> => {
  const res = await axiosInstance.get("/api/v1/dashboard/injection-result", {
    params,
  });
  return unwrap<InjectionResult[]>(res) ?? [];
};

export const fetchDistribution = async (
  dimension: DistributionDimension,
): Promise<DistributionPoint[]> => {
  const res = await axiosInstance.get("/api/v1/dashboard/distribution", {
    params: { dimension },
  });
  return unwrap<DistributionPoint[]>(res) ?? [];
};

export const fetchVanCodes = async (): Promise<CommonCode[]> => {
  const res = await axiosInstance.get("/api/v1/common/van");
  return unwrap<CommonCode[]>(res) ?? [];
};

export const fetchWorkerPerformance = async (
  params: DateRangeParams = {},
): Promise<WorkerPerformance[]> => {
  const res = await axiosInstance.get("/api/v1/dashboard/worker-performance", {
    params,
  });
  return unwrap<WorkerPerformance[]>(res) ?? [];
};

export const fetchWeekdayHourHeatmap = async (
  params: DateRangeParams = {},
): Promise<WeekdayHourCount[]> => {
  const res = await axiosInstance.get(
    "/api/v1/dashboard/weekday-hour-heatmap",
    { params },
  );
  return unwrap<WeekdayHourCount[]>(res) ?? [];
};

export const fetchAuditSummary = async (
  params: DateRangeParams = {},
): Promise<AuditSummary | null> => {
  const res = await axiosInstance.get("/api/v1/dashboard/audit-summary", {
    params,
  });
  return unwrap<AuditSummary>(res);
};

export interface RecentInjectedTerminal {
  terminalId: number;
  terminalStatus: number;
  serialNo?: string;
  deviceType?: string;
  vanType: string | number;
  terminalColor?: string;
  keyInjectionYn?: string;
  spSerialInjectionYn?: string;
  createdDate?: string;
  updatedDate?: string;
  groupId?: string;
  spSerialNo?: string;
  macAddress?: string;
  macInjectionYn?: string;
  securityKeyRef1?: string;
  securityKeyRef2?: string;
  refInjectionYn?: string;
  internalModelName?: string;
  externalModelName?: string;
  terminalModelId?: number;
  dikYn?: string;
  aliasName?: string;
  createdByName?: string;
  lastInjectedAt?: string;
}

export const fetchRecentInjectedTerminals = async (): Promise<
  RecentInjectedTerminal[]
> => {
  const res = await axiosInstance.get("/api/v1/dashboard/recent-terminals");
  return unwrap<RecentInjectedTerminal[]>(res) ?? [];
};

